import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../lib/api-client";
import { queryKeys } from "../query-keys";
import {
  ApplicationSchema,
  ApplicationAckSchema,
  ApplicationAnswerSchema,
  BenchAckSchema,
  type Application,
  type ApplicationAnswerPayload,
  type CreateApplicationRequest,
  type UpdateApplicationStatusRequest,
} from "@applicant-reviewer/api";

// ---------------------------------------------------------------------------
// Create application (start a new application flow)
// ---------------------------------------------------------------------------
export function useCreateApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateApplicationRequest) =>
      apiClient.post("/applications", ApplicationSchema, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.applications.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Submit application (finalise)
// ---------------------------------------------------------------------------
export function useSubmitApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (applicationId: string) =>
      apiClient.post(`/applications/${applicationId}/submit`, ApplicationAckSchema),
    onSuccess: (_data, applicationId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.applications.detail(applicationId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.applications.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.pipeline.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Save answer step
// ---------------------------------------------------------------------------
interface SaveAnswerStepVariables {
  applicationId: string;
  questionId: string;
  payload: ApplicationAnswerPayload;
}

export function useSaveAnswerStep() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, questionId, payload }: SaveAnswerStepVariables) =>
      apiClient.put(
        `/applications/${applicationId}/answers/${questionId}`,
        ApplicationAnswerSchema,
        payload,
      ),
    onSuccess: (_data, { applicationId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.applications.detail(applicationId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Candidate bench opt-in
// ---------------------------------------------------------------------------
export function useBenchOptIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (applicationId: string) =>
      apiClient.post(`/applications/${applicationId}/bench`, BenchAckSchema, {}),
    onSuccess: (_data, applicationId) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.applications.detail(applicationId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.applications.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Candidate preference submission (for reject/redirect flow)
// ---------------------------------------------------------------------------
export interface CandidatePreferences {
  roles: string[];
  availability: string[];
  locations: string[];
}

interface SubmitPreferencesVariables {
  applicationId: string;
  preferences: CandidatePreferences;
}

export function useSubmitCandidatePreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, preferences }: SubmitPreferencesVariables) =>
      apiClient.patch(`/applications/${applicationId}/preferences`, ApplicationAckSchema, preferences),
    onSuccess: (_data, { applicationId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.applications.detail(applicationId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Update application status (with optimistic update)
// ---------------------------------------------------------------------------
interface UpdateStatusVariables {
  applicationId: string;
  payload: UpdateApplicationStatusRequest;
}

export function useUpdateApplicationStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId, payload }: UpdateStatusVariables) =>
      apiClient.patch(`/applications/${applicationId}/status`, ApplicationSchema, payload),

    onMutate: async ({ applicationId, payload }) => {
      // Cancel any outgoing refetches so they don't overwrite the optimistic update
      await queryClient.cancelQueries({
        queryKey: queryKeys.applications.detail(applicationId, "manager"),
      });

      const previousApplication = queryClient.getQueryData<Application>(
        queryKeys.applications.detail(applicationId, "manager"),
      );

      if (previousApplication) {
        queryClient.setQueryData<Application>(
          queryKeys.applications.detail(applicationId, "manager"),
          { ...previousApplication, status: payload.status },
        );
      }

      return { previousApplication };
    },

    onError: (_error, { applicationId }, context) => {
      if (context?.previousApplication) {
        queryClient.setQueryData<Application>(
          queryKeys.applications.detail(applicationId, "manager"),
          context.previousApplication,
        );
      }
    },

    onSettled: (_data, _error, { applicationId }) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.applications.detail(applicationId, "manager") });
      void queryClient.invalidateQueries({ queryKey: queryKeys.applications.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.pipeline.lists() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.data() });
    },
  });
}
